/**
 * Mode rules — the one place a GameMode preset name turns into layers.
 *
 * A match is: a base (elimination + optional turn limit) + one objective
 * layer + any number of environment layers + an optional respawn layer.
 * Every GameMode is a preset over those layers; a custom match sets the
 * layer fields on ModeConfig directly. Game logic asks `getModeRules`
 * (or tests `modeState.<layer>` for state) and never branches on `mode`.
 *
 * Pure functions — no side effects.
 */

import type { GameMode, ModeConfig, ModeRules, ObjectiveKey, EnvironmentKey } from '../types';
import { MODE_DISPLAY_NAMES, DEFAULT_RESPAWN_TICKETS, POINTS_DEFAULT_TICKETS } from '../constants';

// =============================================================================
// PRESETS
// =============================================================================

export const MODE_PRESETS: Record<GameMode, ModeRules> = {
  deathmatch:  { objective: 'none',        environments: [],            respawn: false },
  dominion:    { objective: 'stakes',      environments: [],            respawn: false },
  sovereignty: { objective: 'stakes',      environments: [],            respawn: false },
  ctf:         { objective: 'ctf',         environments: [],            respawn: true },
  signalTrace: { objective: 'signalTrace', environments: [],            respawn: true },
  uplink:      { objective: 'uplink',      environments: [],            respawn: true },
  salvageRun:  { objective: 'salvage',     environments: [],            respawn: false },
  excavation:  { objective: 'excavation',  environments: [],            respawn: true },
  blackout:    { objective: 'none',        environments: ['blackout'],  respawn: false },
  lastLight:   { objective: 'none',        environments: ['lastLight'], respawn: false },
};

export const OBJECTIVE_KEYS: ObjectiveKey[] = ['none', 'stakes', 'ctf', 'signalTrace', 'uplink', 'salvage', 'excavation', 'points'];
export const ENVIRONMENT_KEYS: EnvironmentKey[] = ['blackout', 'lastLight'];

/** Objectives whose structures are worked through timed interactions. */
const INTERACTION_OBJECTIVES: ReadonlySet<ObjectiveKey> = new Set(['signalTrace', 'uplink', 'salvage', 'excavation']);

/** Objectives that respawn by default when the config does not say. */
const RESPAWN_BY_DEFAULT: ReadonlySet<ObjectiveKey> = new Set(['ctf', 'signalTrace', 'uplink', 'excavation', 'points']);

// =============================================================================
// RESOLUTION
// =============================================================================

/**
 * Resolve the layers of a match. Explicit layer fields on the config win;
 * anything left undefined comes from the `mode` preset. When the objective
 * is set explicitly and `respawn` is not, respawn follows the objective's
 * default (the preset's would be wrong for a custom objective).
 */
export function getModeRules(config: ModeConfig | undefined): ModeRules {
  const preset = MODE_PRESETS[config?.mode ?? 'deathmatch'] ?? MODE_PRESETS.deathmatch;
  if (!config) return preset;
  const objective = config.objective ?? preset.objective;
  const environments = config.environments ?? preset.environments;
  const respawn = config.respawn
    ?? (config.objective !== undefined ? RESPAWN_BY_DEFAULT.has(objective) : preset.respawn);
  // 69.96 (DECISIONS 316): a battle won by its story objectives (the Story's set, 294) is ranked by them at time
  const story = config.winConditions?.use.includes('objectives') ?? false;
  return { objective, environments: [...environments], respawn, ...(story ? { timeVerdict: 'storyObjectives' as const } : {}) };
}

/** The objective's progress is tracked through structure interactions. */
export function objectiveUsesInteractions(objective: ObjectiveKey): boolean {
  return INTERACTION_OBJECTIVES.has(objective);
}

/** True when the match is plain deathmatch with nothing stacked on it. */
export function isPlainDeathmatch(rules: ModeRules): boolean {
  return rules.objective === 'none' && rules.environments.length === 0;
}

/**
 * Jumppack fuel is unlimited in every match that is not plain deathmatch —
 * objectives and environments both need units to get around.
 */
export function hasUnlimitedJumpFuel(config: ModeConfig | GameMode | undefined): boolean {
  const resolved = typeof config === 'string' ? { mode: config } : config;
  return !isPlainDeathmatch(getModeRules(resolved));
}

/** Default respawn ticket pool when the config does not set one. */
export function defaultRespawnTickets(objective: ObjectiveKey): number {
  if (objective === 'points') return POINTS_DEFAULT_TICKETS;
  return objective === 'ctf' ? DEFAULT_RESPAWN_TICKETS : 10;
}

/**
 * Display name for a match: the preset's name, plus any environment layers
 * stacked on top of it that the preset does not already carry
 * ("Dominion + Blackout").
 */
export function matchDisplayName(config: ModeConfig | undefined): string {
  const mode = config?.mode ?? 'deathmatch';
  const rules = getModeRules(config);
  const presetEnvs = MODE_PRESETS[mode]?.environments ?? [];
  const extras = rules.environments.filter(e => !presetEnvs.includes(e));
  const base = MODE_DISPLAY_NAMES[mode] ?? mode;
  const parts = extras.map(e => MODE_DISPLAY_NAMES[e]);
  if (config?.startingAlert) parts.push('Stealth');   // 57.4: the alert layer is not an environment, but it is a modifier
  if (parts.length === 0) return base;
  return `${base} + ${parts.join(' + ')}`;
}

/** The preset whose layers match exactly, if any (for naming a custom match). */
export function presetForRules(rules: ModeRules): GameMode | null {
  for (const [mode, preset] of Object.entries(MODE_PRESETS) as Array<[GameMode, ModeRules]>) {
    if (preset.objective !== rules.objective || preset.respawn !== rules.respawn) continue;
    if (preset.environments.length !== rules.environments.length) continue;
    if (!preset.environments.every(e => rules.environments.includes(e))) continue;
    return mode;
  }
  return null;
}
